"use client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import style from "../../drama/[dramaSlug]/Drama.module.css";
import { useWatchProgressStore, useUnlockModalStore, useWatchDurationStore } from "../../store/localapi";
import { useCoinStore } from "../../store/coinstore";
import { getCurrentServerTime } from "../../utils/serverTime";
import AudioLanguageSelector, { AudioTrackOption } from "./AudioLanguageSelector";
import { ExitFullscreenIcon, FullscreenIcon, NextIcon, PauseCircleIcon, PauseIcon, PiPIcon, PlayCircleIcon, PlayIcon, PrevIcon, VolumeIcon, VolumeOffIcon } from "./PlayerIcons";
import CaptionSelector from "./CaptionSelector";
import QualitySelector from "./QualitySelector";
import UnlockModal from "./UnlockModal";
import { ensureSubtitlesVisible } from "../../utils/ensureSubtitlesVisible";


/* eslint-disable*/
interface DramaData {
  d_id: string;
  d_name: string;
  d_desc: string;
  d_image: string;
  duration: string;
  epi_id: string;
  epi_name: string;
  type: string;
  added_dt: string;
  thumbnails: string;
  total_d_stream: string;
  unlock_count: string;
  translation: string;
  "240p": string;
  "480p": string;
  "720p": string;
  "nosub720p": string;
  "nosub480p": string;
  "nosub240p": string;
}

interface VideoPlayerProps {
  data: DramaData;
  language: { lang_code: string, lang_name: string }[];
  subtitles: { lang_code: string, lang_file: string }[];
  audioTracks: { lang_code: string; audio_file: string; }[];
  audioLanguages: { lang_code: string; lang_name: string }[];
  nextfn: () => void;
  prevfn: () => void;
  isplay: (status: boolean) => void;
  eindex: number;
  isDubbed?: boolean;
  totalEpisodes: number;
  isNextLocked?: boolean;
}
type Quality = "240p" | "480p" | "720p" | "nosub240p" | "nosub480p" | "nosub720p";
type BaseQuality = "720p" | "240p" | "480p";

function VideoPlayer({ data, language, subtitles, audioTracks, audioLanguages, nextfn, prevfn, isplay, eindex, isDubbed, totalEpisodes }: VideoPlayerProps) {
  const QUALITY_KEY = 'quality';
  const SUBTITLE_KEY = 'caption';
  const AUDIO_LANG_KEY = 'audioLang';
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const playerBoxRef = useRef<HTMLDivElement>(null);
  const lastSecondRef = useRef<number | null>(null);
  const hideControlsTimeoutRef = useRef<number | null>(null); // To store the timeout ID
  const qualityChangeRef = useRef({ inProgress: false, time: 0, playing: false });
  const stateRef = useRef({ isDragging: false, isLoading: true });
  const audioDriftRef = useRef<{ lastAdjust: number }>({ lastAdjust: 0 });
  const audioSeekSyncRef = useRef<{ wasPlaying: boolean }>({ wasPlaying: false });
  const lastAudioURLRef = useRef<string>("");
  const lastSeekTimeRef = useRef<number>(0); // Track last seek operation to prevent double execution

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.7); // Initial volume
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [modal, setModal] = useState(false);
  const [isPip, setIsPip] = useState(false);
  const [isFirefox, setIsFirefox] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [showcontrols, setShowcontrols] = useState(true)
  const [isDraggingSeekbar, setIsDraggingSeekbar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const upsertEpisodeProgress = useWatchProgressStore((s) => s.upsertEpisodeProgress);
  const removeEpisodeProgress = useWatchProgressStore((s) => s.removeEpisodeProgress);
  const addPlaySeconds = useWatchDurationStore((s) => s.addPlaySeconds);

  // Import coin store to track watch duration
  const coins = useCoinStore((state) => state.coinBalance);
  const setCoin = useCoinStore.getState().setCoinBalance;

  // refs to accumulate fractional seconds before persisting to store
  const playCountingRef = useRef<number | null>(null); // last observed video.currentTime used for counting
  const playAccRef = useRef<number>(0); // accumulator for fractional seconds

  const fallbackTimerRef = useRef<number | null>(null);
  const notifTimerRef = useRef<number | null>(null);
  const pipOpenRef = useRef<boolean>(false);
  const networkFailTimerRef = useRef<number | null>(null);
  const currentEpisodeRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    currentEpisodeRef.current = data?.epi_id;
  }, [data?.epi_id]);


  // Global modal store for sync between parent and child
  const globalModalState = useUnlockModalStore((state) => state.isOpen);
  const setGlobalModalState = useUnlockModalStore((state) => state.setModalOpen);

  useEffect(() => {
    // Global error handler for uncaught play() promises
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason?.name === 'NotAllowedError' && event.reason?.message?.includes('play()')) {
        event.preventDefault(); // Prevent the error from showing in console
        const video = videoRef.current;
        const audio = audioRef.current;
        if (video) {
          video.pause();
          audio?.pause();
          setIsPlaying(false);
          isplay(false);
        }
      }
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Sync local modal state with global state
  useEffect(() => {
    setModal(globalModalState);
  }, [globalModalState]);

  // Sync global modal state when local modal changes
  useEffect(() => {
    setGlobalModalState(modal);
  }, [modal, setGlobalModalState]);

  // Pause video and external audio when modal opens, sync UI state when modal closes
  useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;

    if (modal) {
      // Pause video when modal opens
      if (video && !video.paused) {
        video.pause();
        setIsPlaying(false);
        isplay(false);
      }

      // Pause external audio if playing
      if (audio && !audio.paused) {
        audio.pause();
      }
    } else {
      // When modal closes, sync UI state with actual video state
      if (video) {
        const actuallyPlaying = !video.paused && !video.ended;
        setIsPlaying(actuallyPlaying);
        isplay(actuallyPlaying);
      }
    }
    // Note: We don't resume playback when modal closes - keep video paused
  }, [modal, isplay]);

  const [quality, setQuality] = useState<Quality>(() => {
    const savedQuality = localStorage.getItem(QUALITY_KEY) as Quality | null;
    if (savedQuality) return savedQuality;
    return data?.translation === "1" ? ('nosub720p' as Quality) : ('720p' as Quality);
  });

  const [selectedVttLang, setSelectedVttLang] = useState<string>(
    localStorage.getItem(SUBTITLE_KEY) || ''
  );
  const [vttSubtitles, setVttSubtitles] = useState<{ lang_code: string, lang_file: string }[]>([]);
  const [lastVolume, setLastVolume] = useState(0.7); // To store the last non-zero volume level
  // Audio language selection state
  const [selectedAudioLang, setSelectedAudioLang] = useState<string>('en');

  // Normalize audio options: default EN builtin + example HI
  const audioOptions: AudioTrackOption[] = useMemo(() => {
    const englishOption: AudioTrackOption = {
      lang_code: 'en', lang_name: 'English (Default)'
    };

    if (audioTracks && audioTracks.length) {
      const otherLanguages = audioTracks
        .filter(t => t.lang_code !== 'en').map((t) => ({
          lang_code: t.lang_code,
          lang_name: t.lang_code.toUpperCase(),
          audio_file: t.audio_file,
        }));

      return [englishOption, ...otherLanguages];
    }
    // Fallback: only English
    return [englishOption];
  }, [audioTracks]);

  const selectedAudio: AudioTrackOption | undefined = useMemo(
    () => audioOptions.find(o => o.lang_code === selectedAudioLang) || audioOptions[0],
    [audioOptions, selectedAudioLang]
  );

  const externalAudioUrl = selectedAudio?.audio_file || '';
  const isExternalAudioActive = !!externalAudioUrl && selectedAudio?.lang_code !== 'en';

  // Resolve a safe, non-empty video src across available qualities to avoid empty-string src errors
  const resolvedVideoSrc: string | null = useMemo(() => {
    if (!data) return null;
    const nonEmpty = (s?: string) => !!s && s.trim() !== "";
    const candidates: string[] = [];
    const pushIf = (k: string) => { const v = (data as any)[k]; if (nonEmpty(v)) candidates.push(v); };

    const qualityKey = quality as string;
    pushIf(qualityKey);
    if (qualityKey.startsWith('nosub')) {
      pushIf(qualityKey.replace('nosub', ''));
    } else {
      pushIf(`nosub${qualityKey}`);
    }
    // Fallback order by common preference
    const baseOrder: BaseQuality[] = ['720p', '480p', '240p'];
    for (const b of baseOrder) pushIf(b);
    for (const b of baseOrder) pushIf(`nosub${b}`);

    return candidates.length ? candidates[0] : null;
  }, [data, quality]);






















  const updateSubtitles = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    // Disable, remove, then re-add tracks
    for (let i = 0; i < video.textTracks.length; i++) {
      const tt = video.textTracks[i];
      tt.mode = 'disabled';
      if (tt.cues) {
        while (tt.cues.length > 0) {
          try { tt.removeCue(tt.cues[0] as VTTCue); } catch { }
        }
      }
    }
    video.querySelectorAll('track').forEach(t => t.remove());

    if (data?.translation !== "1" || !vttSubtitles?.length) return;

    let loadedTracks = 0;
    const totalTracks = vttSubtitles.length;
    // Function to handle track mode setting after all tracks are loaded
    const setTrackModes = () => {
      for (let i = 0; i < video.textTracks.length; i++) {
        const tt = video.textTracks[i];
        tt.mode = ((tt as any).language === selectedVttLang) ? 'showing' : 'hidden';
      }
    };

    // Function to remove duplicate cues from a text track
    const removeDuplicateCues = (textTrack: TextTrack, langCode: string) => {
      if (!textTrack?.cues || textTrack.cues.length === 0) return;

      const seen = new Set<string>();
      const toRemove: VTTCue[] = [];
      const normText = (t: string) =>
        t
          .replace(/<[^>]*>/g, '')       // strip basic tags
          .replace(/&nbsp;/g, ' ')       // decode common entity
          .replace(/\s+/g, ' ')          // collapse whitespace
          .trim();
      for (let i = 0; i < textTrack.cues.length; i++) {
        const cue = textTrack.cues[i] as VTTCue;
        // round to 2 decimals to catch micro-diff duplicates
        const k = `${cue.startTime.toFixed(2)}|${cue.endTime.toFixed(2)}|${normText(cue.text || '')}`;
        if (seen.has(k)) toRemove.push(cue);
        else seen.add(k);
      }

      if (toRemove.length) {
        let removed = 0;
        toRemove.forEach(c => { try { textTrack.removeCue(c); removed++; } catch { } });
        if (removed) console.log(`✅ Removed ${removed} duplicate cues from ${langCode}`);
      }
    };

    // Function to handle track processing completion
    const handleTrackProcessed = () => {
      loadedTracks++;
      if (loadedTracks === totalTracks) setTrackModes();
    };

    // Add new tracks based on the current subtitles
    if (data?.translation === "1" && vttSubtitles) {
      vttSubtitles.forEach(sub => {
        const track = document.createElement('track');
        track.kind = 'subtitles';
        track.label = sub.lang_code.toUpperCase();
        track.src = `${sub.lang_file}?v=${data?.epi_id}`; // Bust cache
        track.srclang = sub.lang_code;
        if (selectedVttLang === sub.lang_code) track.default = true;

        const onLoad = () => {
          const tt = track.track;
          if (tt) removeDuplicateCues(tt, sub.lang_code);  // <-- dedupe here
          handleTrackProcessed();
        };
        const onErr = () => handleTrackProcessed();
        // const onLoad = () => { loadedTracks++; if (loadedTracks === totalTracks) setTrackModes(); };
        // const onErr = () => { loadedTracks++; if (loadedTracks === totalTracks) setTrackModes(); };
        track.addEventListener('load', onLoad, { once: true });
        track.addEventListener('error', onErr, { once: true });
        video.appendChild(track);
      });

      const expectedEpi = data?.epi_id;
      fallbackTimerRef.current = window.setTimeout(async () => {
        if (currentEpisodeRef.current !== expectedEpi) return;

        // If native cues appeared meanwhile, skip fallback
        let totalCues = 0;
        for (let i = 0; i < (videoRef.current?.textTracks.length || 0); i++) {
          totalCues += videoRef.current!.textTracks[i].cues?.length || 0;
        }
        if (totalCues > 0) return;

        // console.log("%cFallBack rendered!", "color: red; font-size: 20px; font-weight: bold;");
        let ok = await ensureSubtitlesVisible({
          videoEl: videoRef.current,
          vttSubtitles,
          selectedLang: selectedVttLang,
          episodeId: expectedEpi,
          useProxy: false,
        });
        if (!ok && currentEpisodeRef.current === expectedEpi) {
          await ensureSubtitlesVisible({
            videoEl: videoRef.current,
            vttSubtitles,
            selectedLang: selectedVttLang,
            episodeId: expectedEpi,
            useProxy: true,
          });
        }
      }, 500) as unknown as number;
    }

  }, [vttSubtitles, selectedVttLang, data?.translation, data?.epi_id]);














  // Simple effect to handle isDubbed prop changes
  useEffect(() => {
    if (!isDubbed) {
      setSelectedAudioLang('en');
    } else {
      const savedLang = localStorage.getItem(AUDIO_LANG_KEY);
      if (savedLang && audioLanguages?.some(opt => opt.lang_code === savedLang)) {
        setSelectedAudioLang(savedLang);
      } else {
        setSelectedAudioLang('en'); // fallback to English
      }
    }
  }, [isDubbed]);

  // Effect to adjust quality based on translation field when data changes
  useEffect(() => {
    if (!data) return;

    const currentBaseQuality = quality.replace('nosub', '') as BaseQuality;
    const expectedQuality = data.translation === "1" ? `nosub${currentBaseQuality}` as Quality : currentBaseQuality;
    if (quality !== expectedQuality) {
      setQuality(expectedQuality);
      localStorage.setItem(QUALITY_KEY, expectedQuality);
    }
  }, [data?.translation, data?.epi_id, quality]);

  // --- FINAL & CORRECTED: Unified Effect (with Progress Saving) ---
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !data) {
      setIsLoading(true);
      return;
    }

    const isQualityChange = qualityChangeRef.current.inProgress;
    const timeToRestore = isQualityChange ? qualityChangeRef.current.time : 0;
    const shouldBePlaying = isQualityChange ? qualityChangeRef.current.playing : true;

    setIsLoading(true);
    // If no valid source, avoid assigning empty src to prevent browser errors
    if (!resolvedVideoSrc) {
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch { }
      setIsLoading(false);
      return;
    }
    video.src = resolvedVideoSrc;
    video.load();


    const onCanPlay = () => {
      setDuration(video.duration);
      video.currentTime = timeToRestore;
      setCurrentTime(timeToRestore);

      if (shouldBePlaying) {
        handlePlayPause();
      } else {
        video.pause();
        setIsPlaying(false);
        isplay(false);
        setIsLoading(false);
      }

      if (isQualityChange) {
        qualityChangeRef.current.inProgress = false;
      }

      video.removeEventListener("canplay", onCanPlay);
    };

    video.addEventListener("canplay", onCanPlay);
    setVttSubtitles(subtitles);

    return () => {
      video.removeEventListener("canplay", onCanPlay);
    };
  }, [data?.epi_id, quality, subtitles, resolvedVideoSrc]);

  // Keep external audio in sync when language/URL/episode/quality changes (source changes only)
  useEffect(() => {
    const audio = audioRef.current;
    const video = videoRef.current;
    if (!audio || !video) return;

    if (isExternalAudioActive) {
      try {
        const targetUrl = new URL(externalAudioUrl, typeof window !== 'undefined' ? window.location.href : undefined).href;
        if (lastAudioURLRef.current !== targetUrl) {
          audio.src = externalAudioUrl;
          audio.load();
          lastAudioURLRef.current = targetUrl;

          const onCanPlay = () => {
            try {
              audio.currentTime = video.currentTime;
              if (!video.paused) audio.play().catch(() => { });
            } catch { }
            audio.removeEventListener('canplay', onCanPlay);
          };
          audio.addEventListener('canplay', onCanPlay);
        }
      } catch { }
    } else {
      // Switch back to builtin audio without affecting the video element
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      lastAudioURLRef.current = "";
    }
  }, [selectedAudioLang, externalAudioUrl, data?.epi_id, quality, isExternalAudioActive]);

  // Separate effect for volume and muted state changes (don't reload audio source)
  useEffect(() => {
    const audio = audioRef.current;
    const video = videoRef.current;
    if (!audio || !video || !isExternalAudioActive) return;

    // Only update volume and muted state, don't reload source
    audio.playbackRate = video.playbackRate;
    audio.volume = volume;
    audio.muted = isMuted;

    // If audio is ready and not during seek, sync time (using ref to avoid effect dependency)
    if (audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && !stateRef.current.isDragging) {
      try {
        const timeDiff = Math.abs(audio.currentTime - video.currentTime);
        if (timeDiff > 0.5) { // Only sync if significantly out of sync
          audio.currentTime = video.currentTime;
        }
        if (!video.paused && audio.paused) {
          audio.play().catch(() => { });
        }
      } catch { }
    }
  }, [volume, isMuted, isExternalAudioActive]);



  // Effect for subtitle changes
  useEffect(() => {
    updateSubtitles();
  }, [selectedVttLang, vttSubtitles, updateSubtitles]);


  const handleQualityChange = useCallback((selectedQuality: BaseQuality) => {
    const video = videoRef.current;
    if (!video || !data) return;

    // Step 1: Store the current time and play-state in our ref.
    qualityChangeRef.current = {
      inProgress: true,
      time: video.currentTime,
      playing: !video.paused,
    };

    const newQuality = data.translation !== "1" ? selectedQuality : `nosub${selectedQuality}` as Quality;
    localStorage.setItem(QUALITY_KEY, newQuality);

    setIsLoading(true);
    // Step 2: Update quality state. This will trigger the useEffect below.
    setQuality(newQuality);
  }, [data]);

  useEffect(() => { //offline management
    const video = videoRef.current;
    if (!video) return;

    const handleOffline = () => {
      video.pause();
      setIsPlaying(false);
      isplay(false);
    };

    // const handleOnline = () => {
    //   console.log('Internet connection restored.');
    //   if (isPlaying) {
    //     video.play().catch(e => console.error("Autoplay on network restore failed:", e));
    //   }
    // };

    window.addEventListener('offline', handleOffline);
    // window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      // window.removeEventListener('online', handleOnline);
    };
  }, [isPlaying, isplay]);

  useEffect(() => {
    stateRef.current.isDragging = isDraggingSeekbar;
    stateRef.current.isLoading = isLoading;
  }, [isDraggingSeekbar, isLoading]);

  useEffect(() => {
    try {
      setIsFirefox(typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent));
    } catch {
      setIsFirefox(false);
    }
    const video = videoRef.current;
    if (!video) return;
    try {
      video.disablePictureInPicture = false;
      video.setAttribute("playsinline", "true");
    } catch { }
    // Detect desktop / large screens (show PiP only on >=1024px)
    try {
      if (typeof window !== 'undefined' && window.matchMedia) {
        const mq = window.matchMedia('(min-width: 1024px)');
        const update = (ev?: MediaQueryListEvent) => setIsLargeScreen(mq.matches);
        setIsLargeScreen(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', update);
        else mq.addListener(update as any);
        return () => { if (mq.removeEventListener) mq.removeEventListener('change', update); else mq.removeListener(update as any); };
      } else {
        setIsLargeScreen(true);
      }
    } catch { setIsLargeScreen(true); }
  }, [resolvedVideoSrc]);

  // Ensure any scheduled PiP-close notifications are cleared when the tab becomes visible/focused.
  useEffect(() => {
    const clearPipNotifications = async () => {
      try {
        if (notifTimerRef.current) {
          clearTimeout(notifTimerRef.current);
          notifTimerRef.current = null;
        }
      } catch {}
      try {
        if ('serviceWorker' in navigator) {
          const reg = await navigator.serviceWorker.getRegistration();
          if (reg) {
            const notifs = await reg.getNotifications({ tag: 'pip-closed' });
            notifs.forEach(n => n.close());
          }
        }
      } catch {}
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') clearPipNotifications();
    };

    const onFocus = () => { clearPipNotifications(); };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, []);











  // --- Auto-hide controls ---
  const resetHideControlsTimer = useCallback(() => {
    if (hideControlsTimeoutRef.current) {// Clear any existing timeout
      clearTimeout(hideControlsTimeoutRef.current);
    }
    setShowcontrols(true);// Show controls immediately
    // new timeout to hide controls after 3 sec
    if (isPlaying && !isDraggingSeekbar && !isLoading) { // Only hide if playing, not dragging, and not loading
      hideControlsTimeoutRef.current = window.setTimeout(() => {
        setShowcontrols(false);
      }, 3000);
    }
  }, [isPlaying, isDraggingSeekbar, isLoading]);

  const handlePlayerMouseMove = useCallback(() => {
    resetHideControlsTimer();
  }, [resetHideControlsTimer]);

  const handlePlayerMouseLeave = useCallback(() => {
    if (hideControlsTimeoutRef.current) {//mouse leaves the player, hide after a short delay
      clearTimeout(hideControlsTimeoutRef.current);
    }
    if (isPlaying && !isDraggingSeekbar && !isLoading) {
      hideControlsTimeoutRef.current = window.setTimeout(() => {
        setShowcontrols(false);
      }, 500); // Hide a bit quicker when mouse leaves entirely
    }
  }, [isPlaying, isDraggingSeekbar, isLoading]);

  useEffect(() => { // Effect1 to manage auto-hide controls
    const playerBox = playerBoxRef.current;
    if (playerBox) {
      playerBox.addEventListener("mousemove", handlePlayerMouseMove);
      playerBox.addEventListener("mouseleave", handlePlayerMouseLeave);
      playerBox.addEventListener("click", handlePlayerMouseMove); // Also reset on click

      if (isPlaying && !isLoading) {// Initial call to set up the timer if playing
        resetHideControlsTimer();
      }
    }

    return () => {
      if (playerBox) {
        playerBox.removeEventListener("mousemove", handlePlayerMouseMove);
        playerBox.removeEventListener("mouseleave", handlePlayerMouseLeave);
        playerBox.removeEventListener("click", handlePlayerMouseMove);
      }
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, [isPlaying, isLoading, isDraggingSeekbar, handlePlayerMouseMove, handlePlayerMouseLeave]);


  useEffect(() => { //Effect2 initial load changes once
    const video = videoRef.current;
    if (!video) {
      setIsLoading(true);
      return;
    }
    if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, []);


  useEffect(() => {//Effect3 to handle video element changes (play/pause, volume, mute)
    const video = videoRef.current;
    const audio = audioRef.current;
    if (!video) return;

    video.volume = volume;
    // If using external audio, keep video muted to avoid double sound
    video.muted = isExternalAudioActive ? true : isMuted;
    if (audio) {
      audio.volume = volume;
      audio.muted = isMuted;
    }

    //handletimeupdate and handleLoadedMetadata fn was here

    const handleTimeUpdate = () => {
      // Use ref to avoid stale state while dragging
      if (!stateRef.current.isDragging && !qualityChangeRef.current.inProgress) {
        setCurrentTime(video.currentTime);

        // Your watch progress logic is preserved
        const currentSecond = Math.floor(video.currentTime);
        if (currentSecond >= 3 && currentSecond !== lastSecondRef.current) {
          upsertEpisodeProgress({
            d_id: data.d_id,
            d_image: data.d_image,
            d_title: data.d_name,
            e_id: data.epi_id,
            epi_num: eindex,
            total_ep: data.total_d_stream,
            duration: video.currentTime,
          });
          lastSecondRef.current = currentSecond;
        }
        // --- Watch-duration accumulation (active playing only) ---
        try {
          const isPlayingNow = !video.paused && !video.ended;
          if (isPlayingNow) {
            const nowTime = video.currentTime;
            const prev = playCountingRef.current;
            if (prev == null) {
              playCountingRef.current = nowTime;
            } else {
              let delta = nowTime - prev;
              // ignore weird jumps (seeks) larger than 5s
              if (delta > 0 && delta < 5) {
                playAccRef.current += delta;
                // only emit whole seconds to avoid churn
                if (playAccRef.current >= 1) {
                  const emit = Math.floor(playAccRef.current);
                  addPlaySeconds(emit);

                  // Update coin store watchDuration and check for is10/is20 eligibility
                  const currentCoinState = useCoinStore.getState();
                  const newWatchDuration = currentCoinState.coinBalance.watchDuration + emit;
                  // Use server time for date (not system time) to match backend
                  const today = getCurrentServerTime().toISOString().split('T')[0];

                  // Only update if not already claimed (is10/is20 !== "2")
                  if (currentCoinState.coinBalance.is10 !== "2" || currentCoinState.coinBalance.is20 !== "2") {
                    const updates: any = { watchDuration: newWatchDuration, watchDurationDate: today };

                    // Auto-set is10 to "1" when reaching 600 seconds (10 minutes)
                    if (newWatchDuration >= 600 && currentCoinState.coinBalance.is10 === "0") {
                      updates.is10 = "1";
                    }

                    // Auto-set is20 to "1" when reaching 1200 seconds (20 minutes)
                    if (newWatchDuration >= 1200 && currentCoinState.coinBalance.is20 === "0") {
                      updates.is20 = "1";
                    }

                    setCoin(updates);
                  }

                  playAccRef.current -= emit;
                }
              } else {
                // treat as seek/jump: reset accumulator
                playAccRef.current = 0;
              }
              playCountingRef.current = nowTime;
            }
          }
        } catch (e) {
          // ignore counting errors
        }
        // Keep audio in sync with small corrections - but less aggressive during rapid changes
        const a = audioRef.current;
        if (a && isExternalAudioActive && !a.paused) {
          const now = performance.now();
          const diff = a.currentTime - video.currentTime;
          const adiff = Math.abs(diff);
          // Only hard-correct occasionally to avoid audible glitches, and avoid during recent seeks
          const timeSinceLastSeek = now - lastSeekTimeRef.current;
          if (adiff > 0.5 && now - audioDriftRef.current.lastAdjust > 700 && timeSinceLastSeek > 1000) {
            a.currentTime = video.currentTime;
            audioDriftRef.current.lastAdjust = now;
          }
        }
      }
    };

    const handleFullscreenChange = () => {
      setIsFullScreen(document.fullscreenElement === playerBoxRef.current);
      resetHideControlsTimer();
    };

    const handlePipChange = () => {
      const video = videoRef.current as (HTMLVideoElement & { webkitPresentationMode?: string }) | null;
      const safariPipActive = video?.webkitPresentationMode === "picture-in-picture";
      const pipActive = document.pictureInPictureElement !== null || !!safariPipActive;
      setIsPip(pipActive);
      // When PiP closes, sync UI with actual media state and (maybe) show a notification
      const wasOpen = pipOpenRef.current;
      if (!pipActive && wasOpen) {
        try {
          const v = videoRef.current;
          const a = audioRef.current;
          if (v) {
            const actuallyPlaying = !v.paused && !v.ended;
            setIsPlaying(actuallyPlaying);
            isplay(actuallyPlaying);
            // Ensure controls are visible when PiP closes
            setShowcontrols(true);
            if (hideControlsTimeoutRef.current) {
              clearTimeout(hideControlsTimeoutRef.current);
            }
            // If video is paused, ensure external audio is paused too
            if (!actuallyPlaying && a && !a.paused) {
              try { a.pause(); } catch { }
            }
          }
          // Small delayed resync to handle race conditions where 'playing' may fire
          // immediately after PiP close. This ensures UI matches actual media state.
          setTimeout(() => {
            try {
              const vv = videoRef.current;
              if (vv) {
                const nowPlaying = !vv.paused && !vv.ended;
                setIsPlaying(nowPlaying);
                isplay(nowPlaying);
                if (!nowPlaying) {
                  setIsLoading(false);
                  setShowcontrols(true);
                }
              }
            } catch (e) { }
          }, 80);
        } catch (e) { }

          // On PiP close, decide whether this is a Back-to-tab (user returned)
          // or a real PiP-close (user closed PiP). We wait a short window and
          // if the page becomes visible/focused within that window we treat it
          // as Back-to-tab and DO NOT show the app notification. Otherwise show it.
          try {
            if (notifTimerRef.current) {
              clearTimeout(notifTimerRef.current);
              notifTimerRef.current = null;
            }

            let backToTab = false;
            const onBack = () => { backToTab = true; };

            const cleanup = async () => {
              try { document.removeEventListener('visibilitychange', onBack); } catch {}
              try { window.removeEventListener('focus', onBack); } catch {}
              try {
                const reg = await navigator.serviceWorker.getRegistration();
                if (reg) {
                  const notifs = await reg.getNotifications({ tag: 'pip-closed' });
                  notifs.forEach(n => n.close());
                }
              } catch {}
            };

            document.addEventListener('visibilitychange', onBack);
            window.addEventListener('focus', onBack);

            const DECIDE_MS = 300;
            notifTimerRef.current = window.setTimeout(async () => {
              try {
                if (backToTab) {
                  await cleanup();
                  return;
                }
                if (typeof window === 'undefined' || !('Notification' in window)) {
                  await cleanup();
                  return;
                }
                if (Notification.permission !== 'granted') {
                  await cleanup();
                  return;
                }
                const registration = await navigator.serviceWorker.ready;
                const artwork = data?.thumbnails || data?.d_image || '/icons/icon-192.png';
                registration.showNotification('DramaFlicks — Mini-player closed', {
                  body: 'Resuming playback on the main screen. Click to view',
                  icon: artwork,
                  badge: artwork,
                  tag: 'pip-closed',
                  data: { url: window.location.href },
                });
              } catch (e) { }
              await cleanup();
            }, DECIDE_MS) as unknown as number;
          } catch (e) { }
      }
      // update previous state
      pipOpenRef.current = pipActive;
    };

    const handlePlaying = () => {// Video is actively playing
      setIsLoading(false);
      try {
        if (networkFailTimerRef.current) {
          clearTimeout(networkFailTimerRef.current);
          networkFailTimerRef.current = null;
        }
      } catch {}
      if (!isPlaying) { // Only update if state is currently false
        setIsPlaying(true);
        isplay(true); // Inform parent
      }
      try {
        const v = videoRef.current;
        if (v) {
          playCountingRef.current = v.currentTime;
        }
      } catch (e) { }
      // Kick off external audio in sync if active
      if (audio && isExternalAudioActive) {
        try {
          audio.currentTime = video.currentTime;
          if (audio.paused) audio.play();
        } catch (e) {
          // console.log(e)
          // ignore autoplay issues for audio
        }
      }
    };

    const handlePause = () => {
      // Sync UI state when the media is paused by any means (user click, programmatic, or end)
      if (!document.hidden) {
        const video = videoRef.current;
        if (video && video.currentTime > 3) {
          upsertEpisodeProgress({
            d_id: data.d_id,
            d_image: data.d_image,
            d_title: data.d_name,
            e_id: data.epi_id,
            epi_num: eindex,
            total_ep: data.total_d_stream,
            duration: video.currentTime,
          });
          lastSecondRef.current = Math.floor(video.currentTime);
        }
        if (isPlaying) {
          setIsPlaying(false);
          isplay(false);
        }
        if (!isLoading) {
          setIsLoading(false);
        }
        setShowcontrols(true); // Always show controls on pause
        if (hideControlsTimeoutRef.current) {
          clearTimeout(hideControlsTimeoutRef.current);
        }
        if (audio && !audio.paused) {
          audio.pause();
        }
        // flush any accumulated play seconds
        try {
          if (playAccRef.current >= 1) {
            const emit = Math.floor(playAccRef.current);
            addPlaySeconds(emit);
            playAccRef.current -= emit;
          }
        } catch (e) { }
        playCountingRef.current = null;
        try {
          if (networkFailTimerRef.current) {
            clearTimeout(networkFailTimerRef.current);
            networkFailTimerRef.current = null;
          }
        } catch {}
      }
    };


    const handleWaiting = () => {// Video is buffering/waiting for data
      setIsLoading(true);
      // Avoid frequent audio pauses on transient buffering to reduce choppiness
    };

    // Handle network / media load failures (error/abort => immediate; stalled/suspend => debounce)
    const handlePermanentMediaFailure = () => {
      try {
        const video = videoRef.current;
        const audio = audioRef.current;
        setIsLoading(false);
        if (video) {
          try { video.pause(); } catch { }
          const actuallyPlaying = !video.paused && !video.ended;
          setIsPlaying(actuallyPlaying);
          isplay(actuallyPlaying);
        } else {
          setIsPlaying(false);
          isplay(false);
        }
        if (audio && !audio.paused) {
          try { audio.pause(); } catch { }
        }
        setShowcontrols(true);
        if (hideControlsTimeoutRef.current) {
          clearTimeout(hideControlsTimeoutRef.current);
        }
      } catch (e) { }
    };

    const scheduleNetworkFailure = (delay = 4000) => {
      if (networkFailTimerRef.current) {
        clearTimeout(networkFailTimerRef.current);
      }
      networkFailTimerRef.current = window.setTimeout(() => {
        // If still not enough data to play, treat as failure
        try {
          const v = videoRef.current;
          if (!v) return handlePermanentMediaFailure();
          // If we have some data and playback is ongoing, don't force failure
          if (!v.paused && v.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            return; // transient, recovered
          }
          // Otherwise, mark as permanent failure
          handlePermanentMediaFailure();
        } catch (e) { }
      }, delay) as unknown as number;
    };

    const clearNetworkFailureSchedule = () => {
      if (networkFailTimerRef.current) {
        clearTimeout(networkFailTimerRef.current);
        networkFailTimerRef.current = null;
      }
    };

    // NOTE: Some browsers fire 'pause' after 'ended'. We explicitly set UI state to stopped
    // and do not auto-advance, so the control shows a proper Play icon at the end.
    const handleEnded = () => {
      // Stop playback and reflect a stopped state in UI when video ends.
      const v = videoRef.current;
      if (v) {
        // try { v.pause(); } catch {}
        try { v.currentTime = 0; } catch { }
      }
      if (audio) {
        // audio.pause();
        try { audio.currentTime = 0; } catch { }
      }
      // flush any accumulated play seconds when ended
      try {
        if (playAccRef.current >= 1) {
          const emit = Math.floor(playAccRef.current);
          addPlaySeconds(emit);
          playAccRef.current -= emit;
        }
      } catch (e) { }
      playCountingRef.current = null;
      // Remove in-progress entry for finished episode/drama
      if (data?.d_id) {
        try { removeEpisodeProgress(data.d_id, eindex); } catch { }
      }
      setIsPlaying(false);
      isplay(false);
      // Seekbar back to start
      setCurrentTime(0);
      setIsLoading(false);
      setShowcontrols(true);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
      // Do NOT auto-play next content on end; user can use Next button if desired.
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    // video.addEventListener("loadedmetadata", handleLoadedMetadata);
    // video.addEventListener("canplay", handleCanPlay); // Listener for initial readiness
    // video.addEventListener("canplaythrough", handleCanPlayThrough);
    video.addEventListener("ended", handleEnded);
    // permanent failures: pause immediately
    video.addEventListener("error", handlePermanentMediaFailure);
    video.addEventListener("abort", handlePermanentMediaFailure);
    // transient events: debounce and only act if they persist
    const handleStalled = () => scheduleNetworkFailure(5000);
    const handleSuspend = () => scheduleNetworkFailure(5000);
    video.addEventListener("stalled", handleStalled);
    video.addEventListener("suspend", handleSuspend);
    video.addEventListener("pause", handlePause);
    video.addEventListener("waiting", handleWaiting); // Listener for buffering
    video.addEventListener("playing", handlePlaying);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    video.addEventListener("enterpictureinpicture", handlePipChange);
    video.addEventListener("leavepictureinpicture", handlePipChange);
    // Keep playbackRate mirrored for smoother sync
    const handleRateChange = () => {
      if (audio && isExternalAudioActive) {
        audio.playbackRate = video.playbackRate;
      }
    };
    video.addEventListener("ratechange", handleRateChange);

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA && isFinite(video.duration) && duration === 0) {
      setDuration(video.duration);
      setCurrentTime(video.currentTime);
      if (!isPlaying || video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
        setIsLoading(false);
      }
      // console.log('inifinte')
    }

    return () => {    // Cleanup event listeners on component unmount
      video.removeEventListener("timeupdate", handleTimeUpdate);
      // video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      // video.removeEventListener("canplay", handleCanPlay);
      // video.removeEventListener("canplaythrough", handleCanPlayThrough);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("error", handlePermanentMediaFailure);
      video.removeEventListener("abort", handlePermanentMediaFailure);
      video.removeEventListener("stalled", handleStalled);
      video.removeEventListener("suspend", handleSuspend);
      clearNetworkFailureSchedule();
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("playing", handlePlaying);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      video.removeEventListener("enterpictureinpicture", handlePipChange);
      video.removeEventListener("leavepictureinpicture", handlePipChange);
      video.removeEventListener("ratechange", handleRateChange);
    };
  }, [volume, isMuted, nextfn, prevfn, duration, data?.d_id, data?.epi_id, isExternalAudioActive]); // Re-run effect when these states change


  // Function to format time from seconds to MM:SS
  const formatTime = (time: number) => {
    if (isNaN(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  // Handlers for controls
  const handlePlayPause = useCallback(async () => {
    const video = videoRef.current;
    const audio = audioRef.current;
    if (!video) return;

    // Prevent play when modal is open
    if (modal) {
      return;
    }

    if (video.paused || video.ended) {
      try {
        await video.play(); // Attempt to play and wait for the promise to resolve
        resetHideControlsTimer();
        setIsPlaying(true); // Update local state
        isplay(true);
        if (audio && isExternalAudioActive) {
          try {
            audio.currentTime = video.currentTime;
            await audio.play();
          } catch (_) { }
        }
      } catch (error: any) {
        if (error.name === "NotAllowedError") {
          console.warn("handlePlayPause: Autoplay prevented. User interaction needed.");
        } else if (error.name === "AbortError") {
          console.warn("handlePlayPause: Play request interrupted (e.g., by a rapid pause or source change).");
        } else {
          console.error("handlePlayPause: Error playing video:", error);
        }
        video.pause();
        setIsPlaying(false); // Update local state
        isplay(false);     // Inform parent
        setIsLoading(false); // Hide loader if play failed
      }
    } else {
      isplay(false);
      setIsPlaying(false);
      video.pause();
      // console.log('handlePlayPause: `video.pause()` called.');
      if (audio && !audio.paused) {
        audio.pause();
      }
      try {
        if (networkFailTimerRef.current) {
          clearTimeout(networkFailTimerRef.current);
          networkFailTimerRef.current = null;
        }
      } catch {}
    }
  }, [isplay, resetHideControlsTimer, isExternalAudioActive, modal]);

  // Audio language dropdown handler
  const handleAudioLanguageSelect = useCallback((langCode: string) => {
    setSelectedAudioLang(langCode);
    const audio = audioRef.current;
    const video = videoRef.current;
    const newlySelected = audioOptions.find(o => o.lang_code === langCode);
    const url = newlySelected?.audio_file || '';
    if (!audio || !video) return;
    if (!url || langCode === 'en') {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      return;
    }
    // Switch to external audio
    if (lastAudioURLRef.current !== url) {
      audio.src = url;
      audio.load();
      lastAudioURLRef.current = url;
    }
    // Align instantly to current time
    const startAt = video.currentTime;
    const playIf = !video.paused;
    const onCanPlay = () => {
      try {
        audio.currentTime = startAt;
        if (playIf) audio.play().catch(() => { });
      } catch {
        console.log('first')
      }
      audio.removeEventListener('canplay', onCanPlay);
    };
    audio.addEventListener('canplay', onCanPlay);
    if (audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      try {
        audio.currentTime = startAt;
        if (playIf) audio.play().catch(() => { });
      } catch { }
    }
    if (isDubbed) {
      localStorage.setItem(AUDIO_LANG_KEY, langCode);
    }
  }, [audioOptions]);

  const handleMuteUnmute = () => {
    const video = videoRef.current;
    const audio = audioRef.current;
    if (!video) return;

    const target = isExternalAudioActive && audio ? audio : video;
    if (target.muted) {
      target.muted = false;
      target.volume = lastVolume; // Restore to last known volume
      setVolume(lastVolume);
      setIsMuted(false);
    } else {
      setLastVolume(target.volume);
      target.muted = true;
      target.volume = 0;
      setVolume(0);
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e: { target: { value: string } }) => {
    const newVolume = parseFloat(e.target.value);
    const video = videoRef.current;
    const audio = audioRef.current;
    if (!video) return;
    if (isExternalAudioActive && audio) {
      audio.volume = newVolume;
    } else {
      video.volume = newVolume;
    }
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    if (newVolume > 0) {
      setLastVolume(newVolume); // Update lastVolume only if not muted
    }
  };

  // Handler for volume adjustment via keyboard
  const adjustVolume = (delta: number) => {
    const video = videoRef.current;
    const audio = audioRef.current;
    const target = isExternalAudioActive && audio ? audio : video;
    if (target) {
      const newVolume = Math.min(1, Math.max(0, target.volume + delta));
      target.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }
  };

  const handleSeekChange = (e: { target: { value: string } }) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    resetHideControlsTimer();

    // Debounce rapid seek changes to prevent flicker
    const now = performance.now();
    if (now - lastSeekTimeRef.current < 16) { // ~60fps limit
      return;
    }
    lastSeekTimeRef.current = now;

    // While dragging, live-update media time for responsiveness
    if (stateRef.current.isDragging) {
      const v = videoRef.current;
      if (v) v.currentTime = newTime;
      const a = audioRef.current;
      if (a && isExternalAudioActive) {
        a.currentTime = newTime;
      }
    }
  };

  const handleSeekMouseDown = () => {
    setIsDraggingSeekbar(true);
    resetHideControlsTimer();
    // Pause external audio while scrubbing to avoid artifacts
    const a = audioRef.current;
    if (a && isExternalAudioActive) {
      audioSeekSyncRef.current.wasPlaying = !a.paused;
      a.pause();
    }
  };

  const handleSeekMouseUp = (e: React.MouseEvent<HTMLInputElement> | React.TouchEvent<HTMLInputElement>) => {
    // Don't set time again here since it's already set in handleSeekChange
    // Just handle the state changes and audio resume
    if (audioRef.current && isExternalAudioActive) {
      if (audioSeekSyncRef.current.wasPlaying) {
        audioRef.current.play().catch(() => { });
      }
    }
    setIsDraggingSeekbar(false);
    resetHideControlsTimer();
  };

  // Handler for seeking adjustment via keyboard
  const adjustSeek = (delta: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(
        videoRef.current.duration,
        Math.max(0, videoRef.current.currentTime + delta)
      );
      setCurrentTime(videoRef.current.currentTime);
      if (audioRef.current && isExternalAudioActive) {
        audioRef.current.currentTime = videoRef.current.currentTime;
      }
      resetHideControlsTimer();
    }
  };


  const handleFullScreen = () => {
    const video = videoRef.current;
    const playerBox = playerBoxRef.current;

    if (!playerBox) {
      console.warn("Player box reference not found for fullscreen.");
      return;
    }

    if (video && (video as any).webkitEnterFullscreen) {
      if (!(document as any).webkitFullscreenElement) {
        (video as any).webkitEnterFullscreen();
      } else {
        (document as any).webkitExitFullscreen();
      }
    } else if (!document.fullscreenElement) {
      playerBox.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name}). This is often blocked by browser security policies in embedded environments (like iframes).`);
      });
    } else {
      document.exitFullscreen();
    }
    resetHideControlsTimer();
  };

  const handlePip = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    resetHideControlsTimer();

    const doc = typeof document !== "undefined" ? document : null;
    const hasStandardPiP = !!doc?.pictureInPictureEnabled && typeof video.requestPictureInPicture === "function" && !(video as any).disablePictureInPicture;
    const webkitVideo = video as typeof video & {
      webkitPresentationMode?: string;
      webkitSetPresentationMode?: (mode: string) => void;
      webkitSupportsPresentationMode?: (mode: string) => boolean;
    };
    const hasWebkitPiP = typeof webkitVideo?.webkitSupportsPresentationMode === "function"
      && webkitVideo.webkitSupportsPresentationMode("picture-in-picture")
      && typeof webkitVideo?.webkitSetPresentationMode === "function";

    try {
      if (doc?.pictureInPictureElement && doc.pictureInPictureElement === video) {
        await doc.exitPictureInPicture();
        return;
      }

      if (hasStandardPiP) {
        await video.requestPictureInPicture();
        return;
      }

      if (hasWebkitPiP) {
        const mode = webkitVideo.webkitPresentationMode === "picture-in-picture" ? "inline" : "picture-in-picture";
        webkitVideo.webkitSetPresentationMode?.(mode);
        return;
      }
    } catch (error) {
      console.error("Unable to toggle Picture-in-Picture", error);
      return;
    }

    const isFirefox = typeof navigator !== "undefined" && /firefox/i.test(navigator.userAgent);
    if (isFirefox) {
      console.warn("Firefox does not yet expose the Picture-in-Picture API. Please use Firefox's built-in PiP button on the video overlay.");
    } else {
      console.warn("Picture-in-Picture not supported by this browser.");
    }
  }, [resetHideControlsTimer]);


  const changeToNextEpisode = useCallback(() => {
    if (eindex + 1 >= totalEpisodes) return;
    nextfn();
    setIsPlaying(true);
    resetHideControlsTimer();
  }, [eindex, totalEpisodes, nextfn, resetHideControlsTimer]);

  const changeToPrevEpisode = useCallback(() => {
    if (eindex === 0) return;
    prevfn();
    setIsPlaying(true);
    resetHideControlsTimer();
  }, [eindex, prevfn, resetHideControlsTimer]);


  const handleNextVideo = useCallback(() => {
    if (isPip) {
      console.warn("Exit Picture-in-Picture before switching episodes.");
      return;
    }
    changeToNextEpisode();
  }, [isPip, changeToNextEpisode]);

  const handlePrevVideo = useCallback(() => {
    if (isPip) {
      console.warn("Exit Picture-in-Picture before switching episodes.");
      return;
    }
    changeToPrevEpisode();
  }, [isPip, changeToPrevEpisode]);

  const handleNextVideoFromMediaControls = useCallback(() => {
    // When triggered from PiP/OS media controls, just switch episode.
    // Because we keep the same <video> element (no changing key),
    // the active PiP window will automatically show the new episode
    // without closing or moving.
    changeToNextEpisode();
  }, [changeToNextEpisode]);

  const handlePrevVideoFromMediaControls = useCallback(() => {
    changeToPrevEpisode();
  }, [changeToPrevEpisode]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    const mediaSession = navigator.mediaSession;
    if (!mediaSession) return;

    const artworkSrc = data?.thumbnails || data?.d_image;
    if (typeof MediaMetadata !== "undefined") {
      try {
        mediaSession.metadata = new MediaMetadata({
          title: data?.epi_name || data?.d_name || "DramaFlicks",
          artist: data?.d_name || "DramaFlicks",
          album: data?.d_name || "DramaFlicks",
          artwork: artworkSrc ? [{ src: artworkSrc, sizes: "512x512", type: "image/jpeg" }] : undefined,
        });
      } catch { }
    }

    const attachHandler = (action: MediaSessionAction, handler: MediaSessionActionHandler | null) => {
      try {
        mediaSession.setActionHandler(action, handler);
      } catch { }
    };

    if (isPip) {
      // disable next/prev while in PiP to avoid navigation from PiP UI
      attachHandler("nexttrack", null);
      attachHandler("previoustrack", null);
    } else {
      attachHandler("nexttrack", handleNextVideoFromMediaControls);
      attachHandler("previoustrack", handlePrevVideoFromMediaControls);
    }

    return () => {
      attachHandler("nexttrack", null);
      attachHandler("previoustrack", null);
    };
  }, [
    data?.d_id,
    data?.d_name,
    data?.epi_id,
    data?.epi_name,
    data?.thumbnails,
    handleNextVideoFromMediaControls,
    handlePrevVideoFromMediaControls,
    isPip,
  ]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    const mediaSession = navigator.mediaSession;
    if (!mediaSession) return;
    try {
      mediaSession.playbackState = isPlaying ? "playing" : "paused";
    } catch { }
  }, [isPlaying]);

  const chooseCaption = (langCode: string) => {
    setSelectedVttLang(langCode);
    localStorage.setItem(SUBTITLE_KEY, langCode);
    resetHideControlsTimer();
  }


  useEffect(() => {  //Effect5 to Keyboard shortcuts
    const handleKeyDown = (event: KeyboardEvent) => {
      if (document.activeElement && (document.activeElement.tagName === "INPUT" || document.activeElement.tagName === "TEXTAREA")) return;
      if (event.ctrlKey || event.altKey || event.shiftKey || event.metaKey) return;

      switch (event.key) {
        case " ": event.preventDefault(); handlePlayPause(); break;
        case "m": case "M": handleMuteUnmute(); break;
        case "n": case "N": handleNextVideo(); break;
        case "p": case "P": handlePrevVideo(); break;
        case "ArrowLeft": event.preventDefault(); adjustSeek(-5); break;
        case "ArrowRight": event.preventDefault(); adjustSeek(5); break;
        case "ArrowUp": event.preventDefault(); adjustVolume(0.05); break;
        case "ArrowDown": event.preventDefault(); adjustVolume(-0.05); break;
        case "f": case "F": handleFullScreen(); break;
        case "i": case "I": handlePip(); break;
        default: break;
      }
      resetHideControlsTimer();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => { document.removeEventListener("keydown", handleKeyDown) };
  }, [isPlaying, isMuted, volume, currentTime, isFullScreen, isPip, handleNextVideo, handlePrevVideo, handlePip]);








  return (
    <div
      className={style.playerbox}
      ref={playerBoxRef}
    >
      {/* <div className={style.inner_box}> */}
      {isLoading && <div className={style.centerbtn}></div>}
      {/* Original condition: modal && isFullScreen - now shows in both modes */}
      {modal && (
        <div className={style.modal_overlay}>
          <UnlockModal style={style} close={() => setModal(false)} />
        </div>
      )}
      <video
        id="videoPlayer"
        ref={videoRef}
        playsInline={true}
        //@ts-ignore
        webkitplaysinline={"true"}
        crossOrigin="anonymous"
        preload="auto"
        poster={data?.thumbnails || data?.d_image}
        muted={isExternalAudioActive ? true : isMuted}
        onClick={handlePlayPause}
        src={resolvedVideoSrc || undefined}
        onDoubleClick={handleFullScreen}
      >Your browser does not support the video tag.
        {data?.translation === "1" && vttSubtitles && vttSubtitles.map((sub, index) => (
          <track
            key={sub.lang_code + index}
            kind="subtitles"
            label={sub.lang_code.toUpperCase()}
            src={sub.lang_file + '?v=' + data?.epi_id}
            srcLang={sub.lang_code}
            default={selectedVttLang === sub.lang_code}
          />
        ))}
      </video>
      {/* hidden external audio element used for dubbed languages */}
      <audio ref={audioRef} preload="auto" crossOrigin="anonymous" style={{ display: 'none' }} />

      {/* loader  */}
      {isLoading && <div className={style.centerbtn} style={{ background: 'none' }}>
        <div className={style.spinner}></div>
      </div>}

      {/* play-center-btn */}
      <div className={style.centerbtn} onClick={handlePlayPause} title="Space toggle"
        style={{ opacity: isPlaying && !showcontrols ? 0 : 1, pointerEvents: isPlaying && !showcontrols ? "none" : "auto" }}>

        {isPlaying && !isLoading && <PlayIcon />}
        {!isPlaying && !isLoading && <PauseIcon />}
      </div>

      {/* updown (next/prev) */}
      {!isPip && (
        <div
          className={style.arrows}
          style={{ opacity: isPlaying && !showcontrols ? 0 : 1, pointerEvents: isPlaying && !showcontrols ? "none" : "auto" }}>
          <span onClick={handleNextVideo} title="Press N"><NextIcon isNext={eindex + 1 >= totalEpisodes} /></span>
          <span onClick={handlePrevVideo} title="Press P"><PrevIcon eindex={eindex} /></span>
        </div>
      )}

      <div className={style.overlay} style={{ opacity: isPlaying && !showcontrols ? 0 : 1, pointerEvents: isPlaying && !showcontrols ? "none" : "auto" }}>
        {/* seekbar  */}
        <div className={style.seekbar}>
          <div className={style.playpausebtn} onClick={handlePlayPause}>
            {isPlaying ? <PlayCircleIcon /> : <PauseCircleIcon />}
          </div>
          <div className={style.progressBar} title="Click or drag to seek/ Left/Right arrow to seek 5s">
            <input
              type="range"
              min="0"
              max={duration}
              value={currentTime}
              onChange={handleSeekChange}
              onMouseDown={handleSeekMouseDown}
              onMouseUp={handleSeekMouseUp}
              onTouchStart={handleSeekMouseDown}
              onTouchEnd={handleSeekMouseUp}
              id={style.range}
            // style={{["--p" as string]: `${(currentTime / duration) * 100}%`,width: "80%",}}
            />
          </div>
        </div>

        {/* controls  */}
        <div className={style.controlbtns}>
          {/* timer */}
          <span className={style.timer}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
          <div className={style.iconbtns}>

            {/* volume icon  */}
            <div className={style.volumebox}>
              <span onClick={handleMuteUnmute} title="M toggle">
                {isMuted || volume === 0 ? <VolumeOffIcon /> : <VolumeIcon />}
              </span>
              <input id={style.volume} style={{ marginRight: '4px' }} type="range" min="0" max="1" step="0.01" value={isMuted ? 0 : volume} onChange={handleVolumeChange} />
              {/* style={{["--p" as string]: `${volume * 100}%`,width: "80%",}} */}
            </div>

            {/* quality icon  */}
            <QualitySelector quality={quality} handleQualityChange={handleQualityChange} />

            {/* caption icon */}
            {data?.translation === "1" && <CaptionSelector selectedVttLang={selectedVttLang} chooseCaption={chooseCaption} data={data} language={language} />}

            {/* audio language icon */}
            {isDubbed && <AudioLanguageSelector
              selectedLang={selectedAudioLang}
              onSelect={handleAudioLanguageSelect}
              isDubbed={isDubbed}
              audioLanguages={audioLanguages}
              audioTracks={audioTracks}
            />}

            {/* PIP  */}
            {!isFirefox && isLargeScreen && (
              <span className={style.pip} onClick={handlePip} title="Picture-in-picture">
                <PiPIcon style={style.pip} />
              </span>
            )}
            
            {/* full screen icon  */}
            <span className={style.fullscreen} onClick={handleFullScreen} title="F toggle">
              {isFullScreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
            </span>
          </div>
        </div>
      </div>
      {/* </div> */}
    </div>
  );
}

export default VideoPlayer;



{/* PIP  */ }
{/* <span className={style.pip} onClick={handlePip}>
  <svg
  xmlns="http://www.w3.org/2000/svg"
  height="24px"
  viewBox="0 -960 960 960"
  width="24px"
  fill="#FFFFFF"
  className={style.pip}
  >
  <path d="M80-520v-80h144L52-772l56-56 172 172v-144h80v280H80Zm80 360q-33 0-56.5-23.5T80-240v-200h80v200h320v80H160Zm640-280v-280H440v-80h360q33 0 56.5 23.5T880-720v280h-80ZM560-160v-200h320v200H560Z" />
  </svg>
</span> */}

